import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/axios';

const CircuitDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = (storedUser.id || storedUser._id)?.toString();
    const userRole = storedUser.role || 'player';

    const [circuitData, setCircuitData] = useState(null);
    const [availableTournaments, setAvailableTournaments] = useState([]);
    const [selectedTournament, setSelectedTournament] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchCircuit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchCircuit = async () => {
        try {
            const { data } = await api.get(`/circuits/${id}`);
            setCircuitData(data);
            
            // If the user is a Master, fetch their tournaments so they can attach them
            if (userRole === 'master' || userRole === 'admin') {
                const myTournaments = await api.get('/tournaments');
                // Filter out tournaments that are already in this circuit
                const alreadyAttachedIds = data.circuit.tournaments.map(t => t._id);
                const attachable = myTournaments.data.filter(t => !alreadyAttachedIds.includes(t._id));
                setAvailableTournaments(attachable);
            }
        } catch (err) {
            console.error(err);
            alert("Circuit not found.");
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleAttachTournament = async () => {
        if (!selectedTournament) return;
        try {
            await api.post(`/circuits/${id}/tournaments`, { tournamentId: selectedTournament });
            setSelectedTournament('');
            fetchCircuit(); // Refresh everything to update the leaderboard!
        } catch (err) {
            alert("Failed to attach tournament.");
        }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center font-black text-blue-600">Loading Global Leaderboard...</div>;
    if (!circuitData) return null;

    const { circuit, leaderboard } = circuitData;
    const isMaster = userRole === 'admin' || (userRole === 'master' && circuit.masterId === userId);

    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6">
            <div className="max-w-6xl mx-auto space-y-8">
                
                {/* --- HEADER --- */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8 flex justify-between items-center">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="bg-purple-100 text-purple-700 font-black px-3 py-1 rounded-lg text-xs uppercase tracking-widest">
                                CHAMPIONSHIP CIRCUIT
                            </span>
                        </div>
                        <h1 className="text-4xl font-black text-gray-900 tracking-tight">{circuit.name}</h1>
                        {circuit.description && <p className="text-gray-500 mt-2 font-medium">{circuit.description}</p>}
                    </div>
                    <button onClick={() => navigate('/dashboard')} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-bold transition-colors">
                        Back to Dashboard
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    
                    {/* --- LEFT: THE GLOBAL LEADERBOARD --- */}
                    <div className="lg:col-span-2 bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-5 border-b border-gray-100 bg-gray-50">
                            <h3 className="font-black text-gray-800 text-lg">Global Leaderboard</h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-100 text-[10px] font-black uppercase tracking-widest text-gray-400">
                                        <th className="px-6 py-4 text-center">Rank</th>
                                        <th className="px-6 py-4">Player</th>
                                        <th className="px-6 py-4 text-center">Events Played</th>
                                        <th className="px-6 py-4 text-center text-blue-600">Total Points</th>
                                        <th className="px-6 py-4 text-center">Tiebreaker (Game Pts)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {leaderboard.length === 0 ? (
                                        <tr><td colSpan="5" className="p-8 text-center text-gray-400 font-bold italic">No points recorded yet. Attach a completed tournament!</td></tr>
                                    ) : (
                                        leaderboard.map((player, index) => (
                                            <tr key={player.id} className="hover:bg-blue-50/30 transition-colors">
                                                <td className="px-6 py-4 text-center">
                                                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-black text-sm ${
                                                        index === 0 ? 'bg-yellow-400 text-yellow-900' : 
                                                        index === 1 ? 'bg-gray-300 text-gray-700' : 
                                                        index === 2 ? 'bg-orange-400 text-orange-900' : 'text-gray-400'
                                                    }`}>
                                                        {index + 1}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 font-black text-gray-800">{player.name}</td>
                                                <td className="px-6 py-4 text-center font-bold text-gray-500">{player.tournamentsPlayed}</td>
                                                <td className="px-6 py-4 text-center font-black text-blue-600 text-lg">{player.totalWinPts}</td>
                                                <td className="px-6 py-4 text-center font-bold text-gray-400">{player.totalGamePts}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* --- RIGHT: TOURNAMENT LIST & CONTROLS --- */}
                    <div className="space-y-6">
                        
                        {/* Attached Tournaments */}
                        <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="px-6 py-5 border-b border-gray-100 bg-gray-50">
                                <h3 className="font-black text-gray-800">Included Events</h3>
                            </div>
                            <div className="divide-y divide-gray-100">
                                {circuit.tournaments.length === 0 ? (
                                    <p className="p-6 text-center text-sm text-gray-400 font-bold">No events attached.</p>
                                ) : (
                                    circuit.tournaments.map(t => (
                                        <Link to={`/tournament/${t._id}`} key={t._id} className="block p-4 hover:bg-gray-50 transition-colors">
                                            <div className="font-bold text-gray-800">{t.name}</div>
                                            <div className="text-xs text-gray-500 font-medium uppercase mt-1">{t.status} • Round {t.currentRound}</div>
                                        </Link>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* MASTER CONTROL: Attach new tournaments */}
                        {isMaster && (
                            <div className="bg-purple-50 rounded-3xl shadow-sm border-2 border-purple-200 p-6">
                                <h3 className="font-black text-purple-800 mb-2">Attach Event</h3>
                                <p className="text-xs text-purple-600 font-bold mb-4">Add an existing tournament to this circuit to merge its points into the global leaderboard.</p>
                                
                                <select 
                                    value={selectedTournament} 
                                    onChange={(e) => setSelectedTournament(e.target.value)}
                                    className="w-full bg-white border border-purple-300 rounded-xl px-4 py-3 font-bold text-gray-700 outline-none mb-3"
                                >
                                    <option value="">Select a tournament...</option>
                                    {availableTournaments.map(t => (
                                        <option key={t._id} value={t._id}>{t.name} ({t.status})</option>
                                    ))}
                                </select>
                                
                                <button 
                                    onClick={handleAttachTournament}
                                    disabled={!selectedTournament}
                                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white font-black py-3 rounded-xl transition-all active:scale-95"
                                >
                                    Merge Points ⚡
                                </button>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default CircuitDetails;